/**
 * Proof of Address Upload Component
 * Based on Figma:
 * - https://www.figma.com/design/8nBzg5I71TF3EWyr6NcpVa/Fiat?node-id=4021-78489 (Initial state)
 * - https://www.figma.com/design/8nBzg5I71TF3EWyr6NcpVa/Fiat?node-id=4021-78531 (Uploading state)
 * - https://www.figma.com/design/8nBzg5I71TF3EWyr6NcpVa/Fiat?node-id=4021-78580 (Uploaded state)
 */
import { FileCheck2, FileText, ImageUp, X } from 'lucide-react';

import { Button } from '@components/ui/button';
import { Spinner } from '@components/ui/spinner';

interface UploadedFile {
  name: string;
  size: number;
  progress?: number; // 0-100, undefined means upload complete
}

interface ProofOfAddressUploadProps {
  isSubmitting: boolean;
  /**
   * Callback when "Maybe later" button is clicked
   */
  onMaybeLater: () => void;
  /**
   * Callback when "Submit" button is clicked
   */
  onSubmit: () => void;
  /**
   * Callback when file is selected
   */
  onFileSelect?: (file: File) => void;
  /**
   * Callback when uploaded file is removed
   */
  onFileRemove?: () => void;
  /**
   * Currently uploaded file (if any)
   */
  uploadedFile?: UploadedFile;
  /**
   * Whether the submit button is disabled
   */
  isSubmitDisabled?: boolean;
}

export function ProofOfAddressUpload({
  isSubmitting,
  onMaybeLater,
  onSubmit,
  onFileSelect,
  onFileRemove,
  uploadedFile,
  isSubmitDisabled = true,
}: ProofOfAddressUploadProps) {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    document.getElementById('file-upload-input')?.click();
  };

  const handleRemoveFile = () => {
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  return (
    <div className="sm:w-[656px]">
      {/* Title */}
      <h2 className="text-foreground text-center">Proof of address</h2>

      {/* Form Content */}
      <div className="flex flex-col gap-4">
        {/* Instructions */}
        <p className="text-foreground text-body2">
          Upload any document listed below to verify your address. Its issuance
          date must be within the past 90 days and it must be clearly visible.
          Partially scanned documents are not accepted.
        </p>

        {/* Accepted Documents */}
        <div className="flex flex-col gap-2">
          <p className="text-foreground text-body1">Accepted Documents</p>

          <div className="flex items-center gap-2">
            <FileText className="size-4" />
            <p className="text-foreground text-body2">Utility bill</p>
          </div>

          <div className="flex items-center gap-2">
            <FileText className="size-4" />
            <p className="text-foreground text-body2">Bank statement</p>
          </div>

          <div className="flex items-center gap-2">
            <FileText className="size-4" />
            <p className="text-foreground text-body2">
              Government correspondence
            </p>
          </div>
        </div>

        {/* Upload Area */}
        <div className="border-border flex flex-col items-center justify-center gap-2 rounded-lg border px-0 py-4">
          <ImageUp className="size-8" />

          <div className="flex flex-col items-center gap-1">
            <p className="text-foreground text-body1">
              Drag and drop to upload, or{' '}
              <button
                type="button"
                onClick={handleBrowseClick}
                className="text-primary cursor-pointer hover:underline"
              >
                Browse
              </button>
            </p>
            <p className="text-muted-foreground text-body2">
              File must be PDF, JPEG, JPG or PNG (Max 10 MB)
            </p>
          </div>

          {/* Hidden file input */}
          <input
            id="file-upload-input"
            type="file"
            accept=".pdf,.jpeg,.jpg,.png"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Uploaded File Display */}
        {uploadedFile && (
          <div className="bg-muted/8 flex items-center gap-2 rounded-lg px-4 py-1.5">
            {/* File Icon */}
            {uploadedFile.progress === undefined ? (
              <FileCheck2 className="size-4 shrink-0" />
            ) : (
              <FileText className="size-4 shrink-0" />
            )}

            {/* File Info */}
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="text-foreground text-body2 truncate">
                {uploadedFile.name}
              </p>

              {/* Progress Bar (uploading) or File Size (uploaded) */}
              {uploadedFile.progress !== undefined ? (
                <div className="bg-border h-1 w-full rounded-lg">
                  <div
                    className="bg-accent-foreground h-1 rounded-lg transition-all"
                    style={{ width: `${uploadedFile.progress}%` }}
                  />
                </div>
              ) : (
                <p className="text-muted-foreground text-micro">
                  {formatFileSize(uploadedFile.size)}
                </p>
              )}
            </div>

            {/* Remove Button */}
            <button
              type="button"
              onClick={handleRemoveFile}
              className="text-foreground hover:text-muted-foreground shrink-0 transition-colors"
              aria-label="Remove file"
            >
              <X className="size-4" />
            </button>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex gap-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onMaybeLater}
          className="flex-1"
        >
          Maybe later
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          className="flex-1"
        >
          Submit {isSubmitting ? <Spinner className="ml-2 size-4" /> : ''}
        </Button>
      </div>
    </div>
  );
}
